import { useEffect, useState, useMemo, useRef } from "react";
import axios from "axios";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import toast from "react-hot-toast";
import { motion, AnimatePresence } from "framer-motion";
import {
  FaUser,
  FaClock,
  FaCalendarAlt,
  FaPlus,
  FaSignOutAlt,
  FaSignInAlt,
  FaEdit,
  FaTrash,
  FaCheckCircle,
  FaExclamationCircle,
  FaChartLine,
  FaDownload,
  FaCamera,
  FaMapMarkerAlt,
  FaHistory,
  FaChevronDown,
} from "react-icons/fa";
import moment from "moment";
import SundayApprovalModal from "../../components/SundayApprovalModal";

const CustomDropdown = ({ options, value, onChange, placeholder, disabled, name, required }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedOption = options.find((opt) => opt.value === value);

  const handleSelect = (optionValue) => {
    onChange({ target: { name, value: optionValue } });
    setIsOpen(false);
  };

  return (
    <div className={`relative ${disabled ? "opacity-50 pointer-events-none" : ""}`} ref={dropdownRef}>
      {required && (
        <input
          tabIndex={-1}
          autoComplete="off"
          style={{ opacity: 0, height: 0, width: 0, position: "absolute" }}
          value={value || ""}
          onChange={() => {}}
          required={required}
        />
      )}
      <div
        className={`w-full bg-slate-50 border-0 rounded-2xl px-4 py-3 text-sm font-bold shadow-inner ring-1 ring-slate-200 focus-within:ring-2 focus-within:ring-indigo-600 outline-none transition-all flex justify-between items-center cursor-pointer ${value ? "text-slate-800" : "text-slate-400"}`}
        onClick={() => !disabled && setIsOpen(!isOpen)}
      >
        <span>{selectedOption ? selectedOption.label : placeholder}</span>
        <motion.div animate={{ rotate: isOpen ? 180 : 0 }}>
          <FaChevronDown className="text-slate-400" />
        </motion.div>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.15 }}
            className="absolute z-50 w-full mt-2 bg-white rounded-xl shadow-xl border border-slate-100 max-h-60 overflow-y-auto"
          >
            <div className="p-1">
              {options.map((opt, index) => (
                <div
                  key={index}
                  className={`px-4 py-2.5 text-sm font-bold rounded-lg cursor-pointer transition-colors ${
                    value === opt.value
                      ? "bg-indigo-50 text-indigo-700"
                      : "text-slate-600 hover:bg-slate-50 hover:text-indigo-600"
                  }`}
                  onClick={() => handleSelect(opt.value)}
                >
                  {opt.label}
                </div>
              ))}
              {options.length === 0 && (
                <div className="px-4 py-3 text-sm text-slate-400 italic text-center">
                  No options available
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

let defaultTaskData = {
  ProjectOrClientName: "",
  Category: "",
  subCategory: "",
  TaskDescription: "",
  task_image: "",
  ConsumingTimeInMin: "",
  PostCreativeStatus: "",
  VideoStatus: "",
  OtherGraphicsName: "",
  OtherGraphicsStatus: "",
};

function UserHome() {
  const [showModal, setShowModal] = useState(false);
  const [isUpdate, setIsUpdate] = useState(false);
  const [isOtherProject, setIsOtherProject] = useState(false);
  const [formData, setFormData] = useState({
    ProjectOrClientName: "",
    Category: "",
    subCategory: "",
    TaskDescription: "",
    task_image: "",
    ConsumingTimeInMin: "",
    PostCreativeStatus: "",
    VideoStatus: "",
    OtherGraphicsName: "",
    OtherGraphicsStatus: "",
    task_date: new Date().toISOString().split("T")[0],
  });

  const [date, setDate] = useState(new Date());
  const [allProject, setAllProject] = useState([]);
  const [allCategory, setAllCategory] = useState([]);
  const [userProject, setUserProject] = useState([]);
  const [taskData, setTaskData] = useState([]);
  const [projects, setProjects] = useState([]);
  const [categorys, setCategory] = useState([]);
  const [subCategorys, setSubCategory] = useState([]);
  const [selectedProjects, setSelectedProjects] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [loading, setLoading] = useState(false);
  const [checkInData, setCheckInData] = useState([]);
  const [checkInSelfieBlob, setCheckInSelfieBlob] = useState(null);
  const [checkOutSelfieBlob, setCheckOutSelfieBlob] = useState(null);
  const [leaveCheck, setLeaveCheck] = useState([]);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showSundayModal, setShowSundayModal] = useState(false);
  const [sundayRequest, setSundayRequest] = useState(null);

  // ── Task Image Capture & Preview States ──
  const [taskImageBlob, setTaskImageBlob] = useState(null);
  const [taskImagePreview, setTaskImagePreview] = useState(null);
  const [showTaskCamera, setShowTaskCamera] = useState(false);
  const [cameraFacing, setCameraFacing] = useState("environment");
  const [previewImageUrl, setPreviewImageUrl] = useState(null);
  const fileInputRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const cameraStreamRef = useRef(null);

  const API_BASE = window.API_BASE || "https://sf.doaguru.com";

  let user = JSON.parse(localStorage.getItem("user"));

  // ═══ Live Timer Logic ═══
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const getLeaveDetails = async () => {
    try {
      const { data } = await axios.get(
        `${API_BASE}/api/getEmployeeTodaysLeavesByUserId/${user?.id}`,
      );
      setLeaveCheck(data);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    getLeaveDetails();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleStatusChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const getTaskImageUrl = (imgPath) => {
    if (!imgPath) return null;
    if (
      imgPath.startsWith("http://") ||
      imgPath.startsWith("https://") ||
      imgPath.startsWith("blob:") ||
      imgPath.startsWith("data:")
    ) {
      return imgPath;
    }
    const clean = imgPath.replace(/^\/+/, "");
    return `${API_BASE}/${clean}`;
  };

  const startCamera = async (facing = cameraFacing) => {
    try {
      setShowTaskCamera(true);
      if (cameraStreamRef.current) {
        cameraStreamRef.current.getTracks().forEach((track) => track.stop());
      }
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: facing },
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
        });
      } catch (err) {
        stream = await navigator.mediaDevices.getUserMedia({ video: true });
      }
      cameraStreamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play().catch(() => {});
      }
    } catch (err) {
      console.error("Camera access error:", err);
      toast.error("Camera access failed or permission denied.");
      setShowTaskCamera(false);
    }
  };

  const stopCamera = () => {
    if (cameraStreamRef.current) {
      cameraStreamRef.current.getTracks().forEach((track) => track.stop());
      cameraStreamRef.current = null;
    }
    setShowTaskCamera(false);
  };

  const switchCameraFacing = () => {
    const nextFacing = cameraFacing === "environment" ? "user" : "environment";
    setCameraFacing(nextFacing);
    startCamera(nextFacing);
  };

  const captureTaskPhoto = () => {
    if (!videoRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current || document.createElement("canvas");
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(
      (blob) => {
        if (blob) {
          setTaskImageBlob(blob);
          const previewUrl = URL.createObjectURL(blob);
          setTaskImagePreview(previewUrl);
          toast.success("Task photo captured successfully!");
        }
        stopCamera();
      },
      "image/webp",
      0.9,
    );
  };

  const handleImageFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        toast.error("Please select a valid image file.");
        return;
      }
      setTaskImageBlob(file);
      setTaskImagePreview(URL.createObjectURL(file));
      toast.success("Image selected!");
    }
  };

  const removeSelectedImage = () => {
    setTaskImageBlob(null);
    setTaskImagePreview(null);
    setFormData((prev) => ({ ...prev, task_image: "" }));
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  useEffect(() => {
    if (showTaskCamera && cameraStreamRef.current && videoRef.current) {
      videoRef.current.srcObject = cameraStreamRef.current;
      videoRef.current.play().catch(() => {});
    }
  }, [showTaskCamera]);

  useEffect(() => {
    return () => {
      if (cameraStreamRef.current) {
        cameraStreamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.TaskDescription?.trim() && !taskImageBlob && !formData.task_image) {
      toast.error("Please enter a Task Description or click a photo of your task.");
      return;
    }

    const submitData = { ...formData };
    if (submitData.subCategory === "POST Create ")
      submitData.PostCreativeCount =
        submitData.PostCreativeStatus === "Complete" ? 1 : 0;
    if (submitData.subCategory === "Video Edittor")
      submitData.VideoCount = submitData.VideoStatus === "Complete" ? 1 : 0;
    if (submitData.subCategory === "Other Graphic Design")
      submitData.OtherGraphicsCount =
        submitData.OtherGraphicsStatus === "Complete" ? 1 : 0;
    const selectedSubCat = subCategorys.find(
      (sub) => sub.name === submitData.subCategory,
    );
    submitData.shootCount = selectedSubCat && selectedSubCat.id == 32 ? 1 : 0;

    const fd = new FormData();
    fd.append("user_id", user.id);
    fd.append("user_full_name", user.full_name);
    Object.keys(submitData).forEach((key) => {
      if (key !== "task_image") {
        fd.append(
          key,
          submitData[key] !== undefined && submitData[key] !== null
            ? submitData[key]
            : "",
        );
      }
    });

    if (taskImageBlob) {
      fd.append("task_image", taskImageBlob, "task_photo.webp");
    } else if (submitData.task_image) {
      fd.append("task_image", submitData.task_image);
    }

    axios
      .post(API_BASE + "/api/add-data", fd)
      .then(() => {
        const selectedDate = submitData.task_date;
        setFormData({ ...defaultTaskData, task_date: selectedDate });
        setTaskImageBlob(null);
        setTaskImagePreview(null);
        setIsOtherProject(false);
        setShowModal(false);
        toast.success("आपका टास्क सफलतापूर्वक जोड़ दिया गया है।");
        fetchTasks(new Date(selectedDate));
      })
      .catch((error) =>
        toast.error(
          error.response?.data?.sqlMessage ||
            error.response?.data ||
            "Error adding task",
        ),
      );
  };

  const fetchTasks = (selectedDate) => {
    const formattedDate = selectedDate.toISOString().split("T")[0];
    axios
      .get(API_BASE + "/api/fetch-data", {
        params: { date: formattedDate },
      })
      .then((response) => {
        let currentUser = response.data.filter(
          (iteam) => iteam.user_id === user.id,
        );
        setTaskData(currentUser);
      })
      .catch((error) => console.error(error));
  };

  const handleEditTask = (task) => {
    setIsUpdate(true);
    setIsOtherProject(false);
    const taskData = {
      ...task,
      task_date:
        task.task_date || task.date || new Date().toISOString().split("T")[0],
    };
    setFormData(taskData);
    setTaskImageBlob(null);
    setTaskImagePreview(task.task_image ? getTaskImageUrl(task.task_image) : null);
    particularProject(task.ProjectOrClientName);
    setSelectedProjects(task.ProjectOrClientName);
    setSelectedCategory(task.Category);
    if (task.ProjectOrClientName) {
      axios
        .get(
          `${API_BASE}/api/category-list?projects_id=${encodeURIComponent(task.ProjectOrClientName)}`,
        )
        .then((response) => {
          setAllCategory(response.data);
          setCategory(response.data);
          if (task.Category) {
            const selectedCategoryObj = response.data.find(
              (cat) => cat.name === task.Category,
            );
            if (selectedCategoryObj) {
              axios
                .get(
                  `${API_BASE}/api/sub-category-list?category_id=${selectedCategoryObj.id}`,
                )
                .then((subResponse) => setSubCategory(subResponse.data));
            }
          }
        });
    }
    setShowModal(true);
  };

  const updateTask = (e) => {
    e.preventDefault();
    if (!formData.TaskDescription?.trim() && !taskImageBlob && !formData.task_image) {
      toast.error("Please enter a Task Description or attach an Image.");
      return;
    }
    const updateData = { ...formData };
    if (updateData.subCategory === "POST Create ")
      updateData.PostCreativeCount =
        updateData.PostCreativeStatus === "Complete" ? 1 : 0;
    if (updateData.subCategory === "Video Edittor")
      updateData.VideoCount = updateData.VideoStatus === "Complete" ? 1 : 0;
    if (updateData.subCategory === "Other Graphic Design")
      updateData.OtherGraphicsCount =
        updateData.OtherGraphicsStatus === "Complete" ? 1 : 0;
    const selectedSubCatUpdate = subCategorys.find(
      (sub) => sub.name === updateData.subCategory,
    );
    updateData.shootCount =
      selectedSubCatUpdate && selectedSubCatUpdate.id == 32 ? 1 : 0;
    if (!updateData.task_date)
      updateData.task_date = new Date().toISOString().split("T")[0];

    const fd = new FormData();
    Object.keys(updateData).forEach((key) => {
      if (key !== "task_image") {
        fd.append(
          key,
          updateData[key] !== undefined && updateData[key] !== null
            ? updateData[key]
            : "",
        );
      }
    });

    if (taskImageBlob) {
      fd.append("task_image", taskImageBlob, "task_photo.webp");
    } else if (updateData.task_image) {
      fd.append("task_image", updateData.task_image);
    } else {
      fd.append("remove_task_image", "true");
    }

    axios
      .post(API_BASE + "/api/update-task", fd)
      .then(() => {
        const taskDate = new Date(updateData.task_date);
        setDate(taskDate);
        fetchTasks(taskDate);
        toast.success("आपका टास्क संपादित हो गया है।");
        setIsUpdate(false);
        setShowModal(false);
        setTaskImageBlob(null);
        setTaskImagePreview(null);
        setFormData({
          ...defaultTaskData,
          task_date: new Date().toISOString().split("T")[0],
        });
        setIsOtherProject(false);
        setSelectedProjects("");
        setSelectedCategory("");
        setAllCategory([]);
        setSubCategory([]);
      })
      .catch((err) => toast.error("टास्क अपडेट करने में त्रुटि हुई"));
  };

  const handleDeleteTask = (id) => {
    if (window.confirm("Are You Sure Remove Today Task !")) {
      axios.post(API_BASE + "/api/delete-task", { id }).then(() => {
        toast.success("Task Removed");
        fetchTasks(date);
      });
    }
  };

  const particularProject = (editProjectName) => {
    const currentProjectName =
      typeof editProjectName === "string"
        ? editProjectName
        : formData.ProjectOrClientName || "";
    Promise.all([
      axios.get(`${API_BASE}/api/getProject/${user?.id}`),
      axios.get(API_BASE + "/api/projects"),
    ])
      .then(([assignRes, projRes]) => {
        const particular_project = assignRes.data || [];
        const all_projects = projRes.data || [];
        setUserProject(particular_project);
        setAllProject(all_projects);

        const userDept = (user?.department || "").trim().toLowerCase();

        // 1. Projects assigned explicitly to this employee by Admin (`assigned_projects`)
        const assignedProjects = particular_project
          .filter((item) => item.project_name || item.name)
          .map((item) => ({
            id: item.project_id || item.id,
            name: item.project_name || item.name,
            department: item.department || "",
          }));

        // 2. Projects assigned/created for the employee's department (`projects.department`)
        const deptProjects = all_projects.filter((p) => {
          if (
            !userDept ||
            !p.department ||
            p.department.trim() === "" ||
            p.department.trim().toLowerCase() === "all"
          ) {
            return false;
          }
          const projectDepts = p.department
            .split(",")
            .map((d) => d.trim().toLowerCase());
          return (
            projectDepts.includes(userDept) ||
            p.department.toLowerCase().includes(userDept)
          );
        });

        // Merge keeping unique projects by name
        const projectMap = new Map();
        assignedProjects.forEach((p) => {
          if (p.name) projectMap.set(p.name.trim().toLowerCase(), p);
        });
        deptProjects.forEach((p) => {
          if (p.name && !projectMap.has(p.name.trim().toLowerCase())) {
            projectMap.set(p.name.trim().toLowerCase(), p);
          }
        });

        // Ensure currently selected / editing project is preserved in options
        if (
          currentProjectName &&
          currentProjectName.trim() !== "" &&
          !projectMap.has(currentProjectName.trim().toLowerCase())
        ) {
          const existingFromAll = all_projects.find(
            (p) =>
              p.name &&
              p.name.trim().toLowerCase() ===
                currentProjectName.trim().toLowerCase(),
          );
          if (existingFromAll) {
            projectMap.set(
              currentProjectName.trim().toLowerCase(),
              existingFromAll,
            );
          } else {
            projectMap.set(currentProjectName.trim().toLowerCase(), {
              id: Date.now(),
              name: currentProjectName,
            });
          }
        }

        const filteredList = Array.from(projectMap.values());
        setProjects(filteredList);
      })
      .catch((error) => {
        console.error("Error loading employee projects:", error);
      });
  };

  const fetchProjectListData = () => {
    particularProject();
  };

  const particularCategory = () => {
    setCategory(allCategory);
  };

  const handleProjectsChange = (e) => {
    const projectId = e?.target ? e.target.value : e;
    if (!projectId) {
      setIsOtherProject(false);
      return;
    }

    if (projectId === "new" || projectId === "Other") {
      setIsOtherProject(true);
      setSelectedProjects("Other");
      setFormData((prev) => ({ ...prev, ProjectOrClientName: "" }));
    } else {
      setIsOtherProject(false);
      setSelectedProjects(projectId);
      setFormData((prev) => ({ ...prev, ProjectOrClientName: projectId }));
    }

    axios
      .get(`${API_BASE}/api/category-list?projects_id=${projectId}`)
      .then((res) => {
        setAllCategory(res.data);
        setCategory(res.data);

        if (!isUpdate) {
          setSubCategory([]);
          setSelectedCategory("");
        }
      });
  };

  const handleCategoryChange = (e) => {
    const categoryId = e.target.value;
    const selectedCategoryObj = allCategory.find((c) => c.name === categoryId);
    if (!selectedCategoryObj) return;
    setSelectedCategory(categoryId);
    setFormData((prev) => ({
      ...prev,
      Category: categoryId,
      CategoryName: selectedCategoryObj.name,
      subCategory: "",
    }));
    axios
      .get(
        `${API_BASE}/api/sub-category-list?category_id=${selectedCategoryObj.id}`,
      )
      .then((res) => setSubCategory(res.data));
  };

  useEffect(() => {
    fetchProjectListData();
    fetchTasks(date);
  }, [date]);

  // ═══ Attendance Lifecycle ═══
  const openCamera = async (onCapture) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      const video = document.createElement("video");
      video.srcObject = stream;
      video.style.transform = "scaleX(-1)";
      video.play();

      const captureLayer = document.createElement("div");
      Object.assign(captureLayer.style, {
        position: "fixed",
        inset: "0",
        background: "rgba(0,0,0,0.9)",
        display: "flex",
        flexFlow: "column",
        alignItems: "center",
        justifyContent: "center",
        zIndex: "99999",
      });

      const captureBtn = document.createElement("button");
      captureBtn.innerText = "CAPTURING IDENTITY...";
      Object.assign(captureBtn.style, {
        marginTop: "20px",
        padding: "12px 30px",
        background: "#4f46e5",
        color: "white",
        borderRadius: "12px",
        fontStyle: "bold",
        cursor: "pointer",
      });

      captureBtn.onclick = () => {
        const canvas = document.createElement("canvas");
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        canvas.getContext("2d").drawImage(video, 0, 0);
        canvas.toBlob((blob) => {
          onCapture(blob);
          stream.getTracks().forEach((t) => t.stop());
        }, "image/webp");
        document.body.removeChild(captureLayer);
      };

      captureLayer.append(video, captureBtn);
      document.body.appendChild(captureLayer);
    } catch (e) {
      alert("Camera failed.");
      setLoading(false);
    }
  };

  const getGeolocation = () =>
    new Promise((res, rej) =>
      navigator.geolocation.getCurrentPosition(
        (p) =>
          res({ latitude: p.coords.latitude, longitude: p.coords.longitude }),
        () => rej("Location denied."),
      ),
    );

  const handleCheckIn = async () => {
    setLoading(true);
    try {
      // ── Sunday Attendance Access Check ──
      const sundayRes = await axios.post(
        `${API_BASE}/api/sunday-login/check-attendance-access`,
        { userId: user?.id }
      );

      if (sundayRes.data && !sundayRes.data.allowed) {
        setLoading(false);
        setSundayRequest(
          sundayRes.data.request || {
            employee_id: user?.id,
            employee_name: user?.full_name,
            employee_email: user?.email_id,
            status: "pending",
            expires_at: sundayRes.data.request?.expires_at,
            expires_at_timestamp: sundayRes.data.request?.expires_at_timestamp,
            request_time: sundayRes.data.request?.request_time,
          }
        );
        setShowSundayModal(true);
        return;
      }

      await openCamera((blob) => setCheckInSelfieBlob(blob));
    } catch (e) {
      console.error(e);
      setLoading(false);
    }
  };

  const handleSundayApprovedSuccess = () => {
    setShowSundayModal(false);
    toast.success("Sunday access approved! Opening camera for attendance...");
    setLoading(true);
    openCamera((blob) => setCheckInSelfieBlob(blob));
  };

  useEffect(() => {
    if (!checkInSelfieBlob) return;
    (async () => {
      const loc = await getGeolocation().catch(() => ({}));
      const fd = new FormData();
      fd.append("user_id", user?.id);
      fd.append("latitude", loc.latitude);
      fd.append("longitude", loc.longitude);
      fd.append("selfie", checkInSelfieBlob, "in.webp");
      try {
        const res = await axios.post(API_BASE + "/api/checkInAttend", fd);
        if (res.data.success) {
          getCheckInData();
          toast.success("Identity Verified: Check-in OK");
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [checkInSelfieBlob]);

  const handleCheckOut = async () => {
    setLoading(true);
    try {
      await openCamera((blob) => setCheckOutSelfieBlob(blob));
    } catch (e) {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!checkOutSelfieBlob) return;
    (async () => {
      const loc = await getGeolocation().catch(() => ({}));
      const fd = new FormData();
      fd.append("user_id", user?.id);
      fd.append("latitude", loc.latitude);
      fd.append("longitude", loc.longitude);
      fd.append("selfie", checkOutSelfieBlob, "out.webp");
      try {
        const res = await axios.put(API_BASE + "/api/checkOutAttend", fd);
        if (res.data.success) {
          getCheckInData();
          toast.success(`Check-out OK: ${res.data.work_minutes} min`);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, [checkOutSelfieBlob]);

  const getCheckInData = () => {
    const stored = localStorage.getItem("user");
    const parsed = stored ? JSON.parse(stored) : user;
    const userId = parsed?.id || parsed?.user_id || parsed?._id;
    if (userId) {
      axios
        .get(`${API_BASE}/api/getCheckInByUser/${userId}`)
        .then((res) => setCheckInData(res.data))
        .catch((e) => console.error(e));
    }
  };
  useEffect(() => {
    getCheckInData();
  }, []);

  // ═══ Metrics Calculation ═══
  const effortMinutes = useMemo(
    () =>
      taskData.reduce((s, t) => s + (parseInt(t.ConsumingTimeInMin) || 0), 0),
    [taskData],
  );
  const effortPercent = Math.min(Math.round((effortMinutes / 480) * 100), 100);

  const shiftDuration = useMemo(() => {
    if (!checkInData[0]?.login_time) return "0:00";
    const login = moment(checkInData[0].login_time, "HH:mm:ss");
    const end = checkInData[0].logout_time
      ? moment(checkInData[0].logout_time, "HH:mm:ss")
      : moment(currentTime);
    const diff = moment.duration(end.diff(login));
    return `${Math.floor(diff.asHours())}:${String(diff.minutes()).padStart(2, "0")}`;
  }, [checkInData, currentTime]);

  return (
    <div className="min-h-screen bg-slate-50 p-3 md:p-6 pb-20 font-sans">
      <div className="max-w-[1400px] mx-auto space-y-4">
        {/* ═══ Dashboard Identity & Status Bar ═══ */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-4 flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-indigo-600 rounded-2xl flex items-center justify-center text-white shadow-xl shadow-indigo-100">
              <FaUser size={20} />
            </div>
            <div>
              <h1 className="text-xl font-black text-slate-800 leading-tight">
                Welcome, {user?.full_name}
              </h1>
              <p className="text-slate-400 font-bold text-[10px] uppercase tracking-tighter">
                Workforce Dashboard • {moment().format("dddd, MMM Do")}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2">
            {leaveCheck.length > 0 ? (
              <span className="bg-rose-50 text-rose-600 px-4 py-2 rounded-xl font-black text-xs border border-rose-100 flex items-center gap-2">
                <FaExclamationCircle /> ON LEAVE
              </span>
            ) : !checkInData[0]?.login_time ? (
              <button
                disabled={loading}
                onClick={handleCheckIn}
                className="bg-emerald-600 hover:bg-emerald-700 text-white font-black px-6 py-2.5 rounded-xl shadow-lg transition-all active:scale-95 flex items-center gap-2 text-xs"
              >
                <FaSignInAlt /> {loading ? "AUTHENTICATING..." : "LOGIN SYSTEM"}
              </button>
            ) : !checkInData[0]?.logout_time ? (
              <button
                disabled={loading}
                onClick={handleCheckOut}
                className="bg-rose-600 hover:bg-rose-700 text-white font-black px-6 py-2.5 rounded-xl shadow-lg transition-all active:scale-95 flex items-center gap-2 text-xs"
              >
                <FaSignOutAlt /> {loading ? "SIGNING OUT..." : "LOGOUT SYSTEM"}
              </button>
            ) : (
              <span className="bg-slate-100 text-slate-500 px-4 py-2.5 rounded-xl font-black text-xs border border-slate-200 flex items-center gap-2">
                <FaCheckCircle /> SHIFT COMPLETED
              </span>
            )}

            <button
              onClick={() => {
                setShowModal(true);
                setIsOtherProject(false);
                particularProject();
              }}
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-black px-6 py-2.5 rounded-xl shadow-lg transition-all active:scale-95 flex items-center gap-2 text-xs"
            >
              <FaPlus /> ADD TASK
            </button>
          </div>
        </div>

        {/* ═══ Metrics Monitor Grid ═══ */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm flex justify-between items-center">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-emerald-50 text-emerald-600 rounded-lg">
                  <FaSignInAlt size={14} />
                </div>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                  Login Time
                </p>
              </div>
              <p className="text-xl font-black text-slate-800 tracking-tight">
                {checkInData[0]?.login_time
                  ? moment(checkInData[0].login_time, "HH:mm:ss").format(
                      "hh:mm A",
                    )
                  : "--:--"}
              </p>
            </div>
            {checkInData[0]?.login_selfie_url && (
              <img
                src={`${"https://sf.doaguru.com"}/${checkInData[0].login_selfie_url}`}
                alt="Login Selfie"
                className="w-12 h-12 rounded-xl object-cover border-2 border-emerald-100 shadow-md cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() =>
                  window.open(
                    `${"https://sf.doaguru.com"}/${checkInData[0].login_selfie_url}`,
                    "_blank",
                  )
                }
                title="Click to view full selfie"
              />
            )}
          </div>

          <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm flex justify-between items-center">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-rose-50 text-rose-600 rounded-lg">
                  <FaSignOutAlt size={14} />
                </div>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                  Logout Time
                </p>
              </div>
              <p className="text-xl font-black text-slate-800 tracking-tight">
                {checkInData[0]?.logout_time
                  ? moment(checkInData[0].logout_time, "HH:mm:ss").format(
                      "hh:mm A",
                    )
                  : "--:--"}
              </p>
            </div>
            {checkInData[0]?.logout_selfie_url && (
              <img
                src={`${"https://sf.doaguru.com"}/${checkInData[0].logout_selfie_url}`}
                alt="Logout Selfie"
                className="w-12 h-12 rounded-xl object-cover border-2 border-rose-100 shadow-md cursor-pointer hover:opacity-80 transition-opacity"
                onClick={() =>
                  window.open(
                    `${"https://sf.doaguru.com"}/${checkInData[0].logout_selfie_url}`,
                    "_blank",
                  )
                }
                title="Click to view full selfie"
              />
            )}
          </div>

          <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm overflow-hidden relative">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-indigo-50 text-indigo-600 rounded-lg">
                <FaClock size={14} />
              </div>
              <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                Shift Duration
              </p>
            </div>
            <p className="text-xl font-black text-indigo-600 tracking-tight">
              {shiftDuration} HRS
            </p>
            {!checkInData[0]?.logout_time && checkInData[0]?.login_time && (
              <div className="absolute bottom-0 left-0 h-1 bg-indigo-500 w-full animate-pulse"></div>
            )}
          </div>

          <div className="bg-white rounded-2xl p-4 border border-slate-200 shadow-sm">
            <div className="flex justify-between items-start mb-2">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-50 text-amber-600 rounded-lg">
                  <FaChartLine size={14} />
                </div>
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                  Effort Goal
                </p>
              </div>
              <p className="text-xs font-black text-amber-600">
                {effortPercent}%
              </p>
            </div>
            <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden mt-3">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${effortPercent}%` }}
                className="h-full bg-amber-500 rounded-full"
              />
            </div>
          </div>
        </div>

        {/* ═══ Activity Ledger ═══ */}
        <div className="bg-white rounded-3xl shadow-sm border border-slate-200 overflow-hidden">
          <div className="p-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
            <div className="flex items-center gap-3">
              <div className="p-1.5 bg-indigo-600 text-white rounded-lg shadow-lg shadow-indigo-100">
                <FaHistory size={14} />
              </div>
              <h2 className="text-sm font-black text-slate-800 uppercase tracking-tight">
                Today's Activity Ledger
              </h2>
            </div>
            <div className="flex items-center gap-2">
              <DatePicker
                selected={date}
                onChange={(d) => setDate(d)}
                dateFormat="yyyy-MM-dd"
                className="px-3 py-1.5 text-xs font-bold rounded-xl border border-slate-200 bg-white shadow-sm focus:ring-2 focus:ring-indigo-100 outline-none w-28 text-center"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-100">
                  <th className="px-4 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                    Project/Client
                  </th>
                  <th className="px-4 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                    Intel Group
                  </th>
                  <th className="px-4 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                    Description
                  </th>
                  <th className="px-4 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest">
                    Time
                  </th>
                  <th className="px-4 py-3 text-[10px] font-black text-slate-400 uppercase tracking-widest text-right">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {taskData.map((task) => (
                  <tr
                    key={task.id}
                    className="hover:bg-slate-50 transition-colors group"
                  >
                    <td className="px-4 py-3">
                      <p className="text-xs font-black text-slate-700">
                        {task.ProjectOrClientName}
                      </p>
                      <p className="text-[10px] text-slate-400 font-bold uppercase tracking-tighter">
                        Category: {task.Category}
                      </p>
                    </td>
                    <td className="px-4 py-3">
                      <span className="bg-indigo-50 text-indigo-600 px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-widest border border-indigo-100">
                        {task.SubCategory}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-xs text-slate-500 font-medium line-clamp-1 max-w-[320px]">
                          {task.TaskDescription || (task.task_image ? "Photo Task Attached" : "--")}
                        </p>
                        {task.task_image && (
                          <button
                            type="button"
                            onClick={() => setPreviewImageUrl(getTaskImageUrl(task.task_image))}
                            className="flex items-center gap-1 px-2.5 py-1 bg-indigo-50 hover:bg-indigo-100 text-indigo-600 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all shadow-sm flex-shrink-0"
                            title="Click to view attached photo"
                          >
                            <FaCamera size={10} /> View Photo
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 text-xs font-black text-slate-700 whitespace-nowrap">
                        <FaClock size={10} className="text-slate-300" />{" "}
                        {task.ConsumingTimeInMin} MIN
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex justify-end gap-1.5">
                        <button
                          onClick={() => handleEditTask(task)}
                          className="p-1.5 text-indigo-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all"
                        >
                          <FaEdit size={14} />
                        </button>
                        <button
                          onClick={() => handleDeleteTask(task.id)}
                          className="p-1.5 text-rose-300 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-all"
                        >
                          <FaTrash size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {taskData.length === 0 && (
                  <tr>
                    <td
                      colSpan="5"
                      className="px-4 py-20 text-center text-slate-300 font-bold italic text-sm"
                    >
                      No activity records synchronized for this date.
                    </td>
                  </tr>
                )}
              </tbody>
              <tfoot className="bg-slate-50 font-black">
                <tr>
                  <td
                    colSpan="3"
                    className="px-4 py-3 text-right text-[10px] text-slate-400 uppercase tracking-widest"
                  >
                    Cumulative Daily Effort:
                  </td>
                  <td className="px-4 py-3 text-sm text-slate-800">
                    {effortMinutes} MIN
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span
                      className={`text-[10px] uppercase tracking-widest px-3 py-1 rounded-full ${effortMinutes >= 480 ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}
                    >
                      {effortMinutes >= 480
                        ? "GOAL ACHIEVED"
                        : `${480 - effortMinutes} MIN REMAINING`}
                    </span>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      </div>

      {/* ═══ Add/Edit Task Modal ═══ */}
      <AnimatePresence>
        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white w-full max-w-2xl rounded-3xl shadow-2xl overflow-hidden border border-slate-100"
            >
              <div className="p-6 border-b border-slate-100 bg-slate-50/50 flex items-center justify-between">
                <h3 className="text-xl font-black text-slate-800 uppercase tracking-tight">
                  {isUpdate
                    ? "Recalibrate Task Effort"
                    : "Initialize Task Entry"}
                </h3>
                <button
                  onClick={() => {
                    setShowModal(false);
                    setIsOtherProject(false);
                    setFormData(defaultTaskData);
                    setTaskImageBlob(null);
                    setTaskImagePreview(null);
                    stopCamera();
                  }}
                  className="bg-white p-2 rounded-xl text-slate-400 hover:text-rose-500 shadow-sm transition-all"
                >
                  &times;
                </button>
              </div>

              <div className="p-6 overflow-y-auto max-h-[70vh]">
                <form
                  onSubmit={!isUpdate ? handleSubmit : updateTask}
                  className="grid grid-cols-1 md:grid-cols-2 gap-5"
                >
                  <div className="space-y-1.5 focus-within:text-indigo-600 transition-colors">
                    <label className="text-[10px] font-black uppercase tracking-widest ml-1">
                      Project/Client
                    </label>
                    <CustomDropdown
                      required={!isOtherProject}
                      name="ProjectOrClientName"
                      value={isOtherProject ? "new" : formData.ProjectOrClientName}
                      onChange={handleProjectsChange}
                      placeholder="Select Project"
                      options={[
                        ...projects.map((p) => ({ value: p.name, label: p.name })),
                        { value: "new", label: "New Project" },
                      ]}
                    />
                    {isOtherProject && (
                      <input
                        required
                        type="text"
                        name="ProjectOrClientName"
                        value={formData.ProjectOrClientName}
                        onChange={(e) =>
                          setFormData((prev) => ({
                            ...prev,
                            ProjectOrClientName: e.target.value,
                          }))
                        }
                        placeholder="Enter Custom Project Name"
                        className="w-full mt-2 bg-slate-50 border-0 rounded-2xl px-4 py-3 text-sm font-bold shadow-inner ring-1 ring-slate-200 focus:ring-2 focus:ring-indigo-600 outline-none transition-all"
                      />
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black uppercase tracking-widest ml-1">
                      Category
                    </label>
                    <CustomDropdown
                      required
                      name="Category"
                      value={formData.Category}
                      onChange={handleCategoryChange}
                      disabled={!selectedProjects}
                      placeholder="Select Category"
                      options={categorys.map((c) => ({ value: c.name, label: c.name }))}
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black uppercase tracking-widest ml-1">
                      Process Pillar
                    </label>
                    <CustomDropdown
                      required
                      name="subCategory"
                      value={formData.subCategory}
                      onChange={handleChange}
                      disabled={!selectedCategory}
                      placeholder="Select Activity"
                      options={subCategorys.map((s) => ({ value: s.name, label: s.name }))}
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black uppercase tracking-widest ml-1">
                      Temporal Impact (MIN)
                    </label>
                    <input
                      required
                      type="number"
                      name="ConsumingTimeInMin"
                      value={formData.ConsumingTimeInMin}
                      onChange={handleChange}
                      className="w-full bg-slate-50 border-0 rounded-2xl px-4 py-3 text-sm font-bold shadow-inner ring-1 ring-slate-200 focus:ring-2 focus:ring-indigo-600 outline-none"
                      placeholder="Minutes Worked"
                    />
                  </div>

                  <div className="md:col-span-2 space-y-1.5">
                    <div className="flex items-center justify-between">
                      <label className="text-[10px] font-black uppercase tracking-widest ml-1">
                        Narrative Description
                      </label>
                      {(taskImageBlob || taskImagePreview || formData.task_image) && (
                        <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2.5 py-0.5 rounded-full border border-emerald-100 flex items-center gap-1">
                          <FaCheckCircle size={10} /> Photo Attached (Description Optional)
                        </span>
                      )}
                    </div>
                    <textarea
                      name="TaskDescription"
                      value={formData.TaskDescription}
                      onChange={handleChange}
                      required={!taskImageBlob && !taskImagePreview && !formData.task_image}
                      rows="3"
                      className="w-full bg-slate-50 border-0 rounded-2xl px-4 py-3 text-sm font-bold shadow-inner ring-1 ring-slate-200 focus:ring-2 focus:ring-indigo-600 outline-none"
                      placeholder={
                        taskImageBlob || taskImagePreview || formData.task_image
                          ? "Optional: Add any notes or leave blank since photo is attached..."
                          : "Elaborate on objectives achieved or click photo below..."
                      }
                    />
                  </div>

                  {/* ═══ Photo Capture / Attachment Section (Under Description) ═══ */}
                  <div className="md:col-span-2 p-4 bg-slate-50/70 rounded-2xl border border-slate-200/80 space-y-3">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div className="flex items-center gap-2">
                        <div className="p-1.5 bg-indigo-50 text-indigo-600 rounded-lg">
                          <FaCamera size={13} />
                        </div>
                        <div>
                          <p className="text-[11px] font-black text-slate-700 uppercase tracking-tight">
                            Task Photo Proof / Screenshot
                          </p>
                          <p className="text-[10px] text-slate-400 font-medium">
                            If you cannot type a description, click or upload a photo to submit your task
                          </p>
                        </div>
                      </div>

                      {/* Hidden File Input */}
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        onChange={handleImageFileSelect}
                        className="hidden"
                      />
                    </div>

                    {/* Preview Box or Action Buttons */}
                    {taskImagePreview ? (
                      <div className="relative flex flex-col sm:flex-row items-center gap-3.5 p-3 bg-white rounded-xl border border-indigo-100 shadow-sm">
                        <div
                          className="relative w-24 h-24 rounded-lg overflow-hidden border border-slate-200 cursor-pointer group flex-shrink-0"
                          onClick={() => setPreviewImageUrl(taskImagePreview)}
                          title="Click to view full image"
                        >
                          <img
                            src={taskImagePreview}
                            alt="Task preview"
                            className="w-full h-full object-cover transition-transform group-hover:scale-105"
                          />
                          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white text-[10px] font-bold">
                            View Full
                          </div>
                        </div>

                        <div className="flex-1 w-full space-y-1">
                          <div className="flex items-center gap-1.5 text-emerald-600 font-black text-xs uppercase tracking-tight">
                            <FaCheckCircle size={12} /> Photo Attached
                          </div>
                          <p className="text-[11px] text-slate-500 font-medium">
                            Ready to submit. Click thumbnail to zoom, or retake/replace.
                          </p>
                          <div className="flex items-center gap-2 pt-1 flex-wrap">
                            <button
                              type="button"
                              onClick={() => startCamera()}
                              className="px-3 py-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-600 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all flex items-center gap-1"
                            >
                              <FaCamera size={11} /> Retake with Camera
                            </button>
                            <button
                              type="button"
                              onClick={() => fileInputRef.current?.click()}
                              className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-600 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all"
                            >
                              Change File
                            </button>
                            <button
                              type="button"
                              onClick={removeSelectedImage}
                              className="px-3 py-1.5 bg-rose-50 hover:bg-rose-100 text-rose-600 rounded-lg text-[10px] font-black uppercase tracking-wider transition-all flex items-center gap-1"
                            >
                              <FaTrash size={10} /> Remove
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col sm:flex-row gap-2.5">
                        <button
                          type="button"
                          onClick={() => startCamera()}
                          className="flex-1 flex items-center justify-center gap-2 py-3 px-4 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-black uppercase tracking-wider shadow-sm transition-all active:scale-95"
                        >
                          <FaCamera size={14} /> Click Photo (Camera)
                        </button>
                        <button
                          type="button"
                          onClick={() => fileInputRef.current?.click()}
                          className="flex-1 flex items-center justify-center gap-2 py-3 px-4 bg-white hover:bg-slate-100 text-slate-700 border border-slate-200 rounded-xl text-xs font-black uppercase tracking-wider transition-all active:scale-95"
                        >
                          📁 Upload / Browse Image
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="space-y-1.5">
                    <label className="text-[10px] font-black uppercase tracking-widest ml-1">
                      Effective Entry Date
                    </label>
                    <input
                      required
                      type="date"
                      name="task_date"
                      value={formData.task_date}
                      onChange={handleChange}
                      className="w-full bg-slate-50 border-0 rounded-2xl px-4 py-3 text-sm font-black shadow-inner ring-1 ring-slate-200 focus:ring-2 focus:ring-indigo-600 outline-none"
                    />
                  </div>

                  {/* Dynamic Status Pickers */}
                  {(formData.subCategory === "POST Create " ||
                    formData.subCategory === "Video Edittor" ||
                    formData.subCategory === "Other Graphic Design") && (
                    <div className="md:col-span-2 p-4 bg-slate-50 rounded-2xl border border-slate-100">
                      <p className="text-[10px] font-black uppercase tracking-widest mb-3">
                        Milestone Progress State
                      </p>
                      <div className="flex flex-wrap gap-3">
                        {["Complete", "In Progress", "ReEditing"].map(
                          (status) => (
                            <label
                              key={status}
                              className={`flex items-center gap-2 px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest cursor-pointer transition-all border ${
                                formData.PostCreativeStatus === status ||
                                formData.VideoStatus === status ||
                                formData.OtherGraphicsStatus === status
                                  ? "bg-indigo-600 text-white border-indigo-600 shadow-lg shadow-indigo-100"
                                  : "bg-white text-slate-400 border-slate-100 hover:border-indigo-200 hover:text-indigo-600 shadow-sm"
                              }`}
                            >
                              <input
                                type="radio"
                                name={
                                  formData.subCategory === "POST Create "
                                    ? "PostCreativeStatus"
                                    : formData.subCategory === "Video Edittor"
                                      ? "VideoStatus"
                                      : "OtherGraphicsStatus"
                                }
                                value={status}
                                checked={
                                  formData.PostCreativeStatus === status ||
                                  formData.VideoStatus === status ||
                                  formData.OtherGraphicsStatus === status
                                }
                                onChange={handleStatusChange}
                                className="hidden"
                              />
                              {status}
                            </label>
                          ),
                        )}
                      </div>
                    </div>
                  )}

                  <div className="md:col-span-2 pt-6 flex gap-3">
                    <button
                      type="submit"
                      className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white font-black py-3 rounded-2xl shadow-xl shadow-indigo-100 transition-all active:scale-95 text-xs"
                    >
                      {isUpdate ? "RECALIBRATE ENTRY" : "SYNCHRONIZE LEDGER"}
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setFormData(defaultTaskData);
                        setTaskImageBlob(null);
                        setTaskImagePreview(null);
                        setIsOtherProject(false);
                      }}
                      className="bg-slate-100 hover:bg-slate-200 text-slate-400 font-black px-6 rounded-2xl transition-all active:scale-95 text-xs uppercase tracking-widest border border-slate-200"
                    >
                      Reset
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* ═══ Live Camera Viewfinder Modal ═══ */}
      {showTaskCamera && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center p-4 bg-slate-950/85 backdrop-blur-md">
          <div className="relative w-full max-w-md bg-slate-900 rounded-3xl overflow-hidden shadow-2xl border border-slate-800 p-5 flex flex-col items-center">
            <div className="w-full flex items-center justify-between pb-3 border-b border-slate-800 text-white">
              <div className="flex items-center gap-2">
                <span className="w-2.5 h-2.5 rounded-full bg-rose-500 animate-ping"></span>
                <h4 className="text-xs font-black uppercase tracking-widest text-slate-200">
                  Task Camera Viewfinder
                </h4>
              </div>
              <button
                type="button"
                onClick={stopCamera}
                className="w-8 h-8 rounded-full bg-slate-800 hover:bg-rose-600/80 text-slate-400 hover:text-white flex items-center justify-center font-bold text-lg transition-all"
              >
                &times;
              </button>
            </div>

            <div className="relative w-full aspect-[4/3] bg-black rounded-2xl overflow-hidden my-4 flex items-center justify-center shadow-inner">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover"
              />
              <canvas ref={canvasRef} className="hidden" />
            </div>

            <div className="w-full flex items-center justify-between gap-3 pt-2">
              <button
                type="button"
                onClick={switchCameraFacing}
                className="px-4 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5"
              >
                🔄 Flip
              </button>
              <button
                type="button"
                onClick={captureTaskPhoto}
                className="px-6 py-3 bg-indigo-600 hover:bg-indigo-500 text-white rounded-2xl font-black text-sm tracking-wide shadow-lg shadow-indigo-600/30 active:scale-95 transition-all flex items-center gap-2"
              >
                <FaCamera size={16} /> Snap Photo
              </button>
              <button
                type="button"
                onClick={stopCamera}
                className="px-4 py-2.5 bg-slate-800 hover:bg-rose-600/20 hover:text-rose-400 text-slate-400 rounded-xl text-xs font-bold transition-all"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ Photo Preview Lightbox Modal ═══ */}
      {previewImageUrl && (
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-md"
          onClick={() => setPreviewImageUrl(null)}
        >
          <div
            className="relative max-w-4xl max-h-[90vh] bg-white rounded-3xl overflow-hidden shadow-2xl p-2 flex flex-col items-center"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-full flex items-center justify-between p-3 border-b border-slate-100">
              <h4 className="text-xs font-black uppercase tracking-wider text-slate-700 flex items-center gap-2">
                <FaCamera className="text-indigo-600" /> Attached Task Photo
              </h4>
              <button
                type="button"
                onClick={() => setPreviewImageUrl(null)}
                className="w-8 h-8 rounded-full bg-slate-100 hover:bg-rose-50 text-slate-500 hover:text-rose-600 flex items-center justify-center font-bold text-lg transition-all"
              >
                &times;
              </button>
            </div>
            <div className="p-2 overflow-auto max-h-[75vh] flex items-center justify-center">
              <img
                src={previewImageUrl}
                alt="Task attachment full preview"
                className="max-h-[70vh] w-auto max-w-full rounded-2xl object-contain shadow-sm"
              />
            </div>
            <div className="w-full p-3 border-t border-slate-100 flex justify-end gap-2 bg-slate-50/50">
              <a
                href={previewImageUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-black uppercase tracking-wider transition-all"
              >
                Open Original
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Sunday Login Approval Modal for Attendance */}
      <SundayApprovalModal
        isOpen={showSundayModal}
        onClose={() => setShowSundayModal(false)}
        requestData={sundayRequest}
        onApprovedSuccess={handleSundayApprovedSuccess}
      />
    </div>
  );
}

export default UserHome;
